/**
 * CoSingery — **登入頁**（Email Magic Link ＋ 6 位一次性數碼；`decisions/041`／`045`）
 *
 * 產品負責人 2026-09-25：「**why need so many words and explaination in the login page, UX so bad**, just
 *   keep the login box, the two button for email login and the one with invitation code, then bottom
 *   **either 6 digit code or invitation no. should auto detect which one appear for input**」
 *   → 版面只有：品牌／一句／email 格／**兩個掣**／**下面一格（自動判斷）**；
 *     所有提示**只在出錯或有狀態時**才出現（唔准常駐長篇解釋 —— `P-200`）。
 *
 * ⚠️ 規則：
 *   · 一律用 i18n（三語）；顏色／字級一律用 token（硬規則 D）
 *   · ⚠️ 登入頁**一定要 `.page__scroll`**（外殼 `overflow:hidden`；唔係手機捲唔到落去 —— `P-197c`）
 *   · **唔准**自己造「已登入」假象：寄唔到信就照實講（`email_send_failed` ＋ 原因）
 *   · 本機開發（`devLink`）會直接顯示連結（server 只喺 localhost 回）
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import BrandMark from '../components/BrandMark';
import { useAuth } from '../hooks/useAuth';
/** ⭐ 0019（`decisions/043`）：服務提供者（公司資料）—— 登入頁頁腳都要有 */
import LegalNotice from '../components/LegalNotice';
/** ⭐ 0044（`decisions/044`）：「已寄出」面板（由登入頁拆出，守住硬規則 B） */
import LoginSentPanel from '../components/LoginSentPanel';
/** ⭐ 0020d：底下一格 —— **自動判斷** 6 位數碼抑或邀請碼（`decisions/045`） */
import LoginCodeOrInvite, { detectLoginField } from '../components/LoginCodeOrInvite';
/** ⭐ 0044：登入頁樣式常數（抽檔守住硬規則 B） */
import { LOGIN_BTN as BTN, LOGIN_CARD as CARD, LOGIN_INPUT as INPUT } from '../styles/loginForm';
/** ⭐ 0018：邀請碼狀態（`?invite=` → localStorage → 交出；只記邀請碼，唔會記 6 位數碼） */
import { useInviteCode } from '../hooks/useInviteCode';


export default function LoginPage() {
  const { t } = useTranslation();
  const { sendLink } = useAuth();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  /** ⭐ 底下嗰格嘅值：由 `?invite=` 帶入／用戶打（**自動判斷**係 6 位數碼定邀請碼） */
  const [field, setField] = useInviteCode(params.get('invite'));
  /** 用戶撳咗〔用邀請碼登入〕／`?invite=` 帶入 → 空白時嗰格當邀請碼 */
  const [wantInvite, setWantInvite] = useState(Boolean(params.get('invite')));
  const [busy, setBusy] = useState(false);
  /** `null` ＝ 未寄；有值 ＝ 已寄（或者本機 devLink／Plan B 連結） */
  const [sent, setSent] = useState<{ email: string; devLink?: string; link?: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** ⭐ 每次「寄出信」或「撳〔用邀請碼登入〕但未填」→ +1：令底下嗰格**自動捲入畫面 ＋ 對焦** */
  const [focusTick, setFocusTick] = useState(0);

  /** 由連結失效跳返嚟（`/login?error=link_invalid`）→ 照實講（唔准一句「發生錯誤」） */
  useEffect(() => {
    if (params.get('error') === 'link_invalid') setError(t('login_error_link_invalid'));
  }, [params, t]);

  /** ⭐ 兩粒掣：`'email'` ＝ 只用 email；`'invite'` ＝ email ＋ 邀請碼（下面嗰格） */
  const submit = async (kind: 'email' | 'invite') => {
    setError(null);
    if (kind === 'invite') {
      setWantInvite(true);
      /** 未打好邀請碼 → 唔好白寄（focus 嗰格 ＋ 一句短提示） */
      if (detectLoginField(field) !== 'invite') {
        setFocusTick((n) => n + 1);
        setError(t('login_error_invite_required_hint'));
        return;
      }
    }
    setBusy(true);
    const res = await sendLink(email.trim(), (document.documentElement.lang || 'zh-HK') as string, kind === 'invite' ? field.trim() : undefined);
    setBusy(false);
    if (!res.ok) {
      /** ⭐ email 未登記 → 自動轉去「邀請碼」意圖 ＋ focus（唔當錯誤，只係話佢知下一步） */
      if (res.code === 'invite_required' && kind === 'email') {
        setWantInvite(true);
        setFocusTick((n) => n + 1);
        setError(t('login_error_invite_required_hint'));
        return;
      }
      setError(
        res.code === 'email_invalid'
          ? t('login_error_email_invalid')
          : res.code === 'invite_required'
            ? t('login_error_invite_required')
            : res.code === 'invite_invalid'
              ? t('login_error_invite_invalid')
              : res.code === 'invite_email_mismatch'
                ? t('login_error_invite_mismatch')
                : res.code === 'email_send_failed'
                  ? `${t('login_error_send_failed')}${res.detail ? `（${res.detail.slice(0, 80)}）` : ''}`
                  : res.code === 'rate_limited'
                    ? t('login_error_too_many')
                    : `${t('login_error_generic')}（${res.code ?? ''}）`,
      );
      return;
    }
    setSent({ email: email.trim(), devLink: res.devLink, link: res.link });
    /** ⭐ 寄完 → 底下嗰格自動捲入畫面 ＋ 對焦（用戶唔需要自己搵） */
    setFocusTick((n) => n + 1);
    /** ⚠️ `emailSent:false` 只有本機／Plan B 先會出現 —— 照實講「未寄出，用呢條連結」 */
    if (res.emailSent === false && !res.devLink && !res.link) setError(t('login_error_send_failed'));
  };

  return (
    /**
     * ⚠️ **一定要 `.page__scroll`**：外殼 `.shell__main` 係 `overflow:hidden`，只有佢可以捲 ——
     *   登入頁之前漏咗 → **手機 Chrome 捲唔到落去、睇唔到打碼格**（其他頁一路都有做）
     */
    <div className="page__scroll" style={{ height: '100%' }} data-testid="login-page">
      {/* ⭐ UI v2 步 11（設計 S9／C20）：`.login` 版面 */}
      <div className="login">
        <div style={{ display: 'grid', justifyItems: 'center', gap: '8px', textAlign: 'center' }}>
          <BrandMark size={56} field />
          <h1 className="card__title" style={{ margin: 0 }}>{t('login_title')}</h1>
        <p className="footnote" style={{ margin: 0 }} data-testid="login-subtitle">
          {t('login_subtitle')}
        </p>
        {/* ⭐ 0018：Beta 測試期（產品負責人：「tell this is a testing period」） */}
        <p className="footnote" style={{ margin: 0 }} data-testid="login-beta-notice">
          {t('login_beta_notice')}
        </p>
      </div>

      <div style={CARD}>
        <label className="field__label" htmlFor="login-email">
          {t('login_email_label')}
        </label>
        <input
          id="login-email"
          type="email"
          inputMode="email"
          autoComplete="email"
          data-testid="login-email"
          className="field__input"
          placeholder={t('login_email_placeholder')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={INPUT}
        />

        {/* ⭐ 產品負責人 2026-09-25：「just keep the login box, **the two button** for email login and the
            one with invitation code」→ 就係呢兩粒掣（唔再有多餘嘅模式切換掣） */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            type="button"
            data-testid="login-send"
            disabled={busy || email.trim().length === 0}
            onClick={() => void submit('email')}
            style={{ ...BTN, opacity: busy || !email.trim() ? 0.6 : 1 }}
          >
            {busy ? t('login_sending') : t('login_send_email_only')}
          </button>
          <button
            type="button"
            data-testid="login-send-invite"
            disabled={busy || email.trim().length === 0}
            onClick={() => void submit('invite')}
            style={{ ...BTN, background: 'transparent', color: 'var(--color-text)', borderColor: 'var(--color-divider)', opacity: busy || !email.trim() ? 0.6 : 1 }}
          >
            {t('login_send_with_invite')}
          </button>
        </div>

        {/* ⭐ 0020d：**底下一格** —— 自動判斷（純數字 ＝ 6 位數碼 → 夠 6 位自動登入；有字母 ＝ 邀請碼） */}
        <LoginCodeOrInvite
          email={email}
          value={field}
          onChange={setField}
          wantInvite={wantInvite}
          focusSignal={focusTick}
          onSignedIn={() => window.location.replace('/')}
          inputStyle={INPUT}
        />

        {/* 照實講：已寄出（去收信）／Plan B（用連結）／本機 devLink（元件 `LoginSentPanel`） */}
        {sent ? <LoginSentPanel email={sent.email} devLink={sent.devLink} directLink={sent.link} /> : null}

        {error ? (
          <p className="notice notice--warn" role="alert" data-testid="login-error">
            {error}
          </p>
        ) : null}

        <p className="footnote" style={{ margin: 0 }}>
          {t('login_privacy_note')}
        </p>
      </div>

      {/* ⭐ 0019（`decisions/043`）：服務提供者（公司名／BR／地址／聯絡；未提供嘅照實唔顯示） */}
      <LegalNotice variant="footer" />
      </div>
    </div>
  );
}
